import axios from 'axios';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useCategoryIds } from 'src/utils/Hooks/reduxSelectors';
import { useFormRetailers } from './useCategoricalFilters';
import queryString from 'qs';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector/types';

export interface BrandSuggestion {
  brandId: string;
  brandName: string;
  name: string;
  id: string;
}

export type UseBrandSuggestionsReturn = UseMutationResult<BrandSuggestion[], Error, string, unknown>;

export const useBrandSuggestions = (): UseBrandSuggestionsReturn => {
  const categoryIds = useCategoryIds();
  const retailers = useFormRetailers();
  const appName = getAppName();

  const fetchBrandSuggestions = async (originalInput: string): Promise<BrandSuggestion[]> => {
    // Only call API when appName is 'atlas'
    if (appName !== AppName.Atlas) {
      return [];
    }

    const trimmedInput = originalInput.trim();

    // Extract retailer IDs for the new multi-retailer endpoint
    const retailerIds = retailers.map(retailer => retailer.id);

    // Single API call to new multi-retailer endpoint
    const paramsString = queryString.stringify({
      term: trimmedInput,
      retailerIds,
      type: 'brand',
      categoryIds
    });
    const requestUrl = `/api/atlas/AutoCompleteSuggestionsMultipleRetailers?${paramsString}`;

    let flatResults: BrandSuggestion[] = [];

    try {
      const response = await axios.get(requestUrl);
      const rawCandidates = response?.data?.suggest?.atlas_brand_suggest?.[0]?.options;

      if (!rawCandidates) {
        flatResults = [];
      } else {
        // Map response to BrandSuggestion format
        flatResults = rawCandidates.map((candidate): BrandSuggestion => {
          const source = candidate._source;
          return {
            brandId: String(source.brandId),
            brandName: source.brandName,
            name: source.name,
            id: String(source.id)
          };
        });
      }
    } catch (error) {
      console.error('Failed to fetch brand suggestions from multiple retailers:', error);
      flatResults = [];
    }

    // Deduplicate based on brandId and brandName combination
    const uniqueResults = flatResults.reduce((acc, current) => {
      const key = current.brandId; // brandId is already a string now
      if (!acc.has(key)) {
        acc.set(key, current);
      }
      return acc;
    }, new Map<string, BrandSuggestion>());

    const finalResults: BrandSuggestion[] = Array.from(uniqueResults.values());

    return finalResults;
  };

  return useMutation<BrandSuggestion[], Error, string>({
    mutationFn: fetchBrandSuggestions,
    onError: (error) => {
      console.error('Failed to fetch brand suggestions:', error);
    }
  });
};

